"use client"

import { FC, useCallback, useMemo } from "react"
import { Phone, Mail, MapPin } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { FaYoutube } from "react-icons/fa"
import { AiOutlineLinkedin, AiFillTikTok } from "react-icons/ai"
import { FaInstagram } from "react-icons/fa6"
import Image from 'next/image'
import { LogoFooter } from '@/public/icons/id'
import { useContent } from "@/src/context/ContentContext"
import { useLanguage } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

const Footer: FC = () => {
  const router = useRouter()
  const { contacts } = useContent()
  const { lang } = useLanguage()
  const t = useUI(lang)

  const navigateToSection = useCallback((sectionId: string) => {
    router.push(`/#${sectionId}`)
  }, [router])

  const currentYear = useMemo(() => new Date().getFullYear(), [])

  return (
    <footer className="pt-16 pb-16 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#121212]">
      <div className="container mx-auto">
        <div className="flex justify-center md:justify-start pb-12">
          <Link href="/" aria-label="На головну" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <Image src={LogoFooter} alt="Logo" />
          </Link>
        </div>
        <div className="w-full flex flex-col md:flex-row md:justify-between md:space-x-8 space-y-12 md:space-y-0 mb-12">
          <div className="flex flex-col items-start">
            <h3 className="text-white font-medium text-lg mb-4">{t("footerSocial")}</h3>
            <div className="space-x-4 flex">
              <a target="_blank" rel="noopener noreferrer" href="https://www.youtube.com/@OBELISK_79011" title="YouTube" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                <FaYoutube className="w-6 h-6" />
              </a>
              <a target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/in/obelisk-%D0%B1%D1%83%D0%B4%D1%96%D0%B2%D0%B5%D0%BB%D1%8C%D0%BD%D0%B0-%D0%BA%D0%BE%D0%BC%D0%BF%D0%B0%D0%BD%D1%96%D1%8F-78039636b/" title="Linkedin" className="text-gray-400 hover:text-white transition-colors" aria-label="Linkedin">
                <AiOutlineLinkedin className="w-6 h-6" />
              </a>
              <a target="_blank" rel="noopener noreferrer" href="https://www.tiktok.com/@obelisk_remont?_t=ZM-8xk2M585ZDS&_r=1" title="TikTok" className="text-gray-400 hover:text-white transition-colors" aria-label="TikTok">
                <AiFillTikTok className="w-6 h-6" />
              </a>
              <a target="_blank" rel="noopener noreferrer" href="https://www.instagram.com/obelisk.remont.lviv?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" title="Instagram" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                <FaInstagram className="w-6 h-6" />
              </a>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h3 className="text-white font-medium text-lg mb-4">{t("footerMenu")}</h3>
            <nav className="space-y-3 font-light">
              <button onClick={() => navigateToSection("hero")} className="block text-gray-400 hover:text-white transition-colors">
                {t("footerAbout")}
              </button>
              <button onClick={() => navigateToSection("pricing")} className="block text-gray-400 hover:text-white transition-colors">
                {t("footerPrice")}
              </button>
              <button onClick={() => navigateToSection("contact-title")} className="block text-gray-400 hover:text-white transition-colors">
                {t("footerConsult")}
              </button>
              <Link href="/contacts" className="block text-gray-400 hover:text-white transition-colors">
                {t("footerContacts")}
              </Link>
            </nav>
          </div>
          <div className="flex flex-col items-start">
            <h3 className="text-white font-medium text-lg mb-4">{t("footerContactsHeading")}</h3>
            <div className="space-y-4 font-light">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a href={contacts.phoneHref} className="text-gray-400 hover:text-white transition-colors">
                  {contacts.phone}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a href={contacts.emailHref} className="text-gray-400 hover:text-white transition-colors">
                  {contacts.email}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a target="_blank" rel="noopener noreferrer" href={contacts.mapHref} className="text-gray-400 hover:text-white transition-colors">
                  {contacts.address}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-400 text-sm font-light">
              © {currentYear} OBELISK. {t("footerCopyright")}
            </p>
            <Link
              href="/privacy-policy"
              className="text-gray-500 text-sm font-light hover:text-white transition-colors"
            >
              {t("footerPrivacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
